"use client";

import { c as _c } from "react/compiler-runtime";
import { Add, AddCircle, Check } from "@mui/icons-material";
import { useRouter } from "next/navigation";
import React, { useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { Button, ButtonSize, ButtonVariant, Checkbox, Input, InputIcon, Progress, ProgressColor, Text, TextColor, TextSize, TextVariant, TextWeight } from "../../components";
import { useMatchBreakpoints } from "../../hooks";
import { useAddressPagination, useCheckoutState } from "../../hooks";
import { AddressCard } from "../AddressCard";
import { PaymentOption, ShippingOption } from "../CheckoutOption";
import { Modal } from "../Modal";
// Import komponentów
import { CheckoutHeader, OrderSection, OrderSummary, StepHeader } from "./components";
const CHECKOUT_STEPS = {
  ADDRESS: 1,
  SHIPPING: 2,
  PAYMENT: 3,
  SUMMARY: 4
};

// Skeleton component for address loading
const AddressCardSkeleton = () => {
  const $ = _c(5);
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-5 bg-gray-200 rounded mb-2 w-3/4" />;
    t1 = <div className="h-4 bg-gray-200 rounded mb-1 w-full" />;
    t2 = <div className="h-4 bg-gray-200 rounded mb-1 w-2/3" />;
    t3 = <div className="h-4 bg-gray-200 rounded mb-4 w-1/2" />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="w-full lg:max-w-[230px] h-[174px] p-4 border border-gray-200 animate-pulse">{t0}{t1}{t2}{t3}<div className="flex gap-2 mt-auto"><div className="h-8 bg-gray-200 rounded w-16" /><div className="h-8 bg-gray-200 rounded w-16" /></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};

// Empty state component for no addresses
const NoAddressesState = t0 => {
  const $ = _c(12);
  const {
    type,
    onAddAddress
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4"><Add className="text-gray-400 !w-8 !h-8" /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = type === "shipping" ? "Brak adres\xF3w do wysy\u0142ki" : "Brak adres\xF3w z danymi do faktury VAT";
  let t3;
  if ($[1] !== t2) {
    t3 = <Text text={t2} variant={TextVariant.H6} weight={TextWeight.SEMIBOLD} className="mb-2 text-center" />;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = type === "shipping" ? "Aby kontynuowa\u0107 zakupy, dodaj sw\xF3j pierwszy adres do wysy\u0142ki" : "Aby wystawi\u0107 faktur\u0119 VAT, dodaj adres z numerem NIP";
  let t5;
  if ($[3] !== t4) {
    t5 = <Text text={t4} fontSize={TextSize.S} color={TextColor.GRAYSCALE_500} className="text-center mb-6 max-w-sm" />;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = type === "shipping" ? "Dodaj adres wysy\u0142ki" : "Dodaj adres z danymi firmy";
  let t7;
  if ($[5] !== onAddAddress || $[6] !== t6) {
    t7 = <Button text={t6} variant={ButtonVariant.PRIMARY} onClick={onAddAddress} size={ButtonSize.XS} className="flex items-center gap-2" />;
    $[5] = onAddAddress;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t3 || $[9] !== t5 || $[10] !== t7) {
    t8 = <div className="flex flex-col items-center justify-center py-12 px-6 border-2 border-dashed border-gray-300 bg-gray-50">{t1}{t3}{t5}{t7}</div>;
    $[8] = t3;
    $[9] = t5;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
};

// Main component
const Checkout = t0 => {
  const $ = _c(115);
  const {
    cart,
    loadingCart,
    loadedCart,
    addresses,
    isAddressesLoading,
    addressForm,
    setAddressCheckout,
    carriers,
    getPayments,
    payments,
    loadingGetPayments,
    isLoggedIn,
    signInForm,
    signUpForm,
    setCarrierCheckout,
    loadingCreateOrder,
    idCarrier,
    setPickupPoint,
    loadingSetPickupPoint
  } = t0;
  const [isSignInFormOpen, setIsSignInFormOpen] = useState(true);
  const [isSignUpFormOpen, setIsSignUpFormOpen] = useState(false);
  const router = useRouter();
  const [openAddressModal, setOpenAddressModal] = useState(false);
  const [addressDetails, setAddressDetails] = useState();
  const {
    isXs,
    isSm,
    isMd
  } = useMatchBreakpoints();
  const isMobile = isXs || isSm || isMd;
  const itemsPerPage = isMobile ? 4 : 6;
  const checkoutState = useCheckoutState();
  const addressPagination = useAddressPagination(addresses, itemsPerPage);
  const hasAnyAddresses = addresses && addresses.length > 0;
  let t1;
  if ($[0] !== addresses) {
    t1 = addresses && addresses.some(_temp);
    $[0] = addresses;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasVatAddresses = t1;
  let t2;
  if ($[2] !== carriers) {
    t2 = carrierId => {
      if (!carrierId) {
        return null;
      }
      return carriers.find(carrier => carrier.id === Number(carrierId));
    };
    $[2] = carriers;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const findCarrierById = t2;
  const isValidAddress2 = _temp2;
  let t3;
  if ($[4] !== addresses || $[5] !== checkoutState || $[6] !== setAddressCheckout) {
    t3 = addressId => {
      const addressShipping = addresses?.find(address_0 => String(address_0.id) === String(addressId));
      if (!isValidAddress2(addressShipping?.address2)) {
        toast.error("Brakuje adresu domu wymaganego przy wysy\u0142ce. Edytuj adres!");
        return;
      } else {
        checkoutState.setSelectedShippingAddress(addressId);
        if (!checkoutState.vatInvoice || checkoutState.selectedBillingAddress) {
          setAddressCheckout(checkoutState.selectedBillingAddress && checkoutState.selectedBillingAddress !== "0" ? Number(checkoutState.selectedBillingAddress) : Number(addressId), Number(addressId));
          checkoutState.setCurrentStep(CHECKOUT_STEPS.SHIPPING);
        }
      }
    };
    $[4] = addresses;
    $[5] = checkoutState;
    $[6] = setAddressCheckout;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleShippingAddressSelect = t3;
  let t4;
  if ($[8] !== addresses || $[9] !== checkoutState || $[10] !== setAddressCheckout) {
    t4 = addressId_0 => {
      const addressBilling = addresses?.find(address_1 => address_1.id === String(addressId_0));
      if (!isValidAddress2(addressBilling?.address2)) {
        toast.error("Brakuje adresu domu wymaganego przy wysy\u0142ce. Edytuj adres!");
        return;
      } else {
        checkoutState.setSelectedBillingAddress(addressId_0);
        if (checkoutState.selectedShippingAddress) {
          setAddressCheckout(Number(addressId_0), checkoutState.selectedShippingAddress && checkoutState.selectedShippingAddress !== "0" ? Number(checkoutState.selectedShippingAddress) : Number(addressId_0));
          checkoutState.setCurrentStep(CHECKOUT_STEPS.SHIPPING);
        }
      }
    };
    $[8] = addresses;
    $[9] = checkoutState;
    $[10] = setAddressCheckout;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleBillingAddressSelect = t4;
  let t5;
  if ($[12] !== cart?.cartId || $[13] !== checkoutState || $[14] !== findCarrierById || $[15] !== setCarrierCheckout || $[16] !== setPickupPoint) {
    t5 = (shippingId, provider, pickupPoint) => {
      checkoutState.setSelectedShipping(shippingId);
      const carrier_0 = findCarrierById(shippingId);
      const requiresPoint = carrier_0 ? checkoutState.requiresDeliveryPointByName(carrier_0.name) : false;
      if (requiresPoint && provider && pickupPoint) {
        setPickupPoint(Number(cart?.cartId ?? 0), provider, pickupPoint);
      }
      if (!requiresPoint || checkoutState.selectedDeliveryPoint) {
        setCarrierCheckout(String(shippingId)).then(() => {
          checkoutState.setCurrentStep(CHECKOUT_STEPS.PAYMENT);
        });
      }
    };
    $[12] = cart?.cartId;
    $[13] = checkoutState;
    $[14] = findCarrierById;
    $[15] = setCarrierCheckout;
    $[16] = setPickupPoint;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleShippingSelect = t5;
  let t6;
  if ($[18] !== checkoutState || $[19] !== getPayments) {
    t6 = point => {
      checkoutState.setSelectedDeliveryPoint(point);
      if (point && checkoutState.selectedShipping) {
        checkoutState.setCurrentStep(CHECKOUT_STEPS.PAYMENT);
        getPayments();
      }
    };
    $[18] = checkoutState;
    $[19] = getPayments;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const handleDeliveryPointSelected = t6;
  let t7;
  if ($[21] !== checkoutState) {
    t7 = paymentId => {
      checkoutState.setSelectedPayment(paymentId);
      checkoutState.setCurrentStep(CHECKOUT_STEPS.SUMMARY);
    };
    $[21] = checkoutState;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const handlePaymentSelect = t7;
  let t8;
  if ($[23] !== addressPagination) {
    t8 = page => {
      addressPagination.setLoadingShippingAddressList(true);
      setTimeout(() => {
        addressPagination.setCurrentShippingAddressPage(page);
        addressPagination.setLoadingShippingAddressList(false);
      }, 300);
    };
    $[23] = addressPagination;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const handleShippingAddressPageChange = t8;
  let t9;
  if ($[25] !== addressPagination) {
    t9 = page_0 => {
      addressPagination.setLoadingBillingAddressList(true);
      setTimeout(() => {
        addressPagination.setCurrentBillingAddressPage(page_0);
        addressPagination.setLoadingBillingAddressList(false);
      }, 300);
    };
    $[25] = addressPagination;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const handleBillingAddressPageChange = t9;
  let t10;
  if ($[27] !== addressPagination) {
    t10 = e => {
      addressPagination.setLoadingShippingAddressList(true);
      addressPagination.setShippingAddressSearch(e.target.value);
      setTimeout(() => {
        addressPagination.setCurrentShippingAddressPage(1);
        addressPagination.setLoadingShippingAddressList(false);
      }, 300);
    };
    $[27] = addressPagination;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const handleShippingAddressSearchChange = t10;
  let t11;
  if ($[29] !== addressPagination) {
    t11 = e_0 => {
      addressPagination.setLoadingBillingAddressList(true);
      addressPagination.setBillingAddressSearch(e_0.target.value);
      setTimeout(() => {
        addressPagination.setCurrentBillingAddressPage(1);
        addressPagination.setLoadingBillingAddressList(false);
      }, 300);
    };
    $[29] = addressPagination;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const handleBillingAddressSearchChange = t11;
  let t12;
  if ($[31] !== addressPagination || $[32] !== checkoutState) {
    t12 = e_1 => {
      checkoutState.setVatInvoice(e_1.target.checked);
      if (!e_1.target.checked) {
        checkoutState.setSelectedBillingAddress(null);
        addressPagination.setBillingAddressSearch("");
        addressPagination.setCurrentBillingAddressPage(1);
      }
    };
    $[31] = addressPagination;
    $[32] = checkoutState;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const handleVatInvoiceChange = t12;
  const getAddressPageNumbers = _temp3;
  let t13;
  if ($[34] !== hasAnyAddresses || $[35] !== hasVatAddresses || $[36] !== itemsPerPage) {
    t13 = (type, addresses_0, paginatedAddresses, selectedAddress, addressSearch, currentPage_0, totalPages_0, loadingList, onAddressSelect, onSearchChange, onPageChange, setOpenAddressModal_0) => {
      const hasAddressesForType = type === "shipping" ? hasAnyAddresses : hasVatAddresses;
      if (!hasAddressesForType) {
        return <div className={`flex flex-col gap-4 ${type === "billing" ? "mt-8" : ""}`}><NoAddressesState type={type} onAddAddress={() => setOpenAddressModal_0(true)} /></div>;
      }
      return <div className="flex flex-col gap-4"><div className="flex justify-between items-center mb-4 flex-col md:flex-row gap-4"><div className="flex items-center gap-2"><Text text={`${type === "shipping" ? "Adres wysy\u0142ki" : "Adres faktury"} (${addresses_0.length})`} variant={TextVariant.H6} weight={TextWeight.MEDIUM} /><div title="Dodaj nowy adres"><AddCircle className="text-gray-500 !w-6 !h-6 cursor-pointer" onClick={() => setOpenAddressModal_0(true)} /></div></div><div className="flex gap-3 items-center flex-col sm:flex-row"><Input id={`${type}-address-search`} name={`${type}-address-search`} value={addressSearch} icon={true} iconPosition="right" iconBackground={true} iconGreyColored={true} iconVariant={InputIcon.SEARCH} placeholder="Wyszukaj adres" onChange={onSearchChange} /></div></div>{addresses_0.length === 0 && addressSearch.trim() && <div className="flex flex-col gap-4 text-center py-8 items-center"><Text text={`Nie znaleziono adresów dla frazy "${addressSearch}"`} fontSize={TextSize.M} className="text-gray-500" /><Text text={"Spr\xF3buj wyszuka\u0107 po imieniu, nazwisku, ulicy, mie\u015Bcie lub kodzie pocztowym"} fontSize={TextSize.S} className="text-gray-400 my-2" /><Button text="Dodaj nowy" variant={ButtonVariant.SECONDARY} onClick={() => {
            setAddressDetails(undefined);
            setOpenAddressModal_0(true);
          }} size={ButtonSize.XS} className="flex items-center gap-2 whitespace-nowrap" /></div>}{loadingList ? <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">{Array.from({
            length: itemsPerPage
          }).map((_, index) => <AddressCardSkeleton key={`${type}-skeleton-${index}`} />)}</div> : paginatedAddresses.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 w-full">{paginatedAddresses.map(address_2 => <AddressCard key={`${type}-${address_2.id}`} address={address_2} onEdit={() => {
            setAddressDetails(address_2);
            setOpenAddressModal_0(true);
          }} className="w-full cursor-pointer" showRadio={true} isSelected={selectedAddress === address_2.id} onSelect={() => onAddressSelect(address_2.id)} radioName={`${type}-address`} />)}</div>}{totalPages_0 > 1 && <div className="flex justify-center items-center space-x-2 flex-col md:flex-row gap-4 mt-4"><Text text="Poprzednia" fontSize={TextSize.XS} color={TextColor.GRAYSCALE_500} onClick={() => {
            if (currentPage_0 === 1) {
              return;
            }
            onPageChange(currentPage_0 - 1);
          }} className={`!h-8 px-2 flex items-center justify-center transition-all duration-200 ${currentPage_0 === 1 ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-gray-50 rounded"}`} /><div className="flex items-center gap-2">{getAddressPageNumbers(currentPage_0, totalPages_0).map((page_1, index_0) => <React.Fragment key={`${type}-page-${index_0}`}>{page_1 === "..." ? <span className="px-3 py-2 text-sm font-medium text-gray-700">...</span> : <button onClick={() => onPageChange(page_1)} className={`w-8 h-8 px-3 flex items-center justify-center text-sm font-medium border transition-all duration-200 ${currentPage_0 === page_1 ? "bg-gray-50 border-gray-800 text-gray-700" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"}`}>{page_1}</button>}</React.Fragment>)}</div>{currentPage_0 < totalPages_0 && <Text text={"Nast\u0119pna"} fontSize={TextSize.XS} color={TextColor.GRAYSCALE_500} onClick={() => {
            if (currentPage_0 === totalPages_0) {
              return;
            }
            onPageChange(currentPage_0 + 1);
          }} className={`!h-8 px-2 flex items-center justify-center transition-all duration-200 ${currentPage_0 === totalPages_0 ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-gray-50 rounded"}`} />}</div>}</div>;
    };
    $[34] = hasAnyAddresses;
    $[35] = hasVatAddresses;
    $[36] = itemsPerPage;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const renderAddressSection = t13;
  let t14;
  if ($[38] !== carriers || $[39] !== checkoutState.selectedShipping || $[40] !== handlePaymentSelect || $[41] !== handleShippingSelect) {
    t14 = (payments_0, selectedId, isCompleted, OptionComponent) => {
      let filteredOptions = isCompleted ? payments_0.filter(option => option.id === selectedId) : payments_0;
      const carrier_2 = carriers.find(carrier_1 => carrier_1.id === Number(checkoutState.selectedShipping));
      if (carrier_2?.name === "Kurier DPD za pobraniem") {
        filteredOptions = filteredOptions.filter(_temp4);
      } else {
        filteredOptions = filteredOptions.filter(_temp5);
      }
      return filteredOptions.map(option_2 => <OptionComponent key={option_2.id} option={option_2} selectedOption={selectedId} setSelectedOption={OptionComponent === ShippingOption ? handleShippingSelect : handlePaymentSelect} />);
    };
    $[38] = carriers;
    $[39] = checkoutState.selectedShipping;
    $[40] = handlePaymentSelect;
    $[41] = handleShippingSelect;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const renderFilteredPaymentsOptions = t14;
  let t15;
  if ($[43] !== checkoutState.requiresDeliveryPointByName || $[44] !== checkoutState.selectedDeliveryPoint || $[45] !== handleDeliveryPointSelected || $[46] !== handleShippingSelect) {
    t15 = (carriers_0, selectedId_0, isCompleted_0) => {
      const filteredOptions_0 = isCompleted_0 ? carriers_0.filter(carrier_3 => carrier_3.id === Number(selectedId_0)) : carriers_0;
      return filteredOptions_0.map(option_3 => <ShippingOption key={option_3.id} option={option_3} selectedOption={selectedId_0} setSelectedOption={handleShippingSelect} selectedDeliveryPoint={checkoutState.selectedDeliveryPoint} onDeliveryPointSelected={handleDeliveryPointSelected} requiresDeliveryPointByName={checkoutState.requiresDeliveryPointByName} />);
    };
    $[43] = checkoutState.requiresDeliveryPointByName;
    $[44] = checkoutState.selectedDeliveryPoint;
    $[45] = handleDeliveryPointSelected;
    $[46] = handleShippingSelect;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  const renderFilteredCarriersOptions = t15;
  const t16 = !!isMobile;
  let t17;
  if ($[48] !== t16) {
    t17 = <CheckoutHeader isMobile={t16} />;
    $[48] = t16;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== loadedCart || $[51] !== loadingCart) {
    t18 = loadingCart && !loadedCart && <div className="flex flex-col gap-4 justify-center items-center h-full min-h-[calc(50vh-64px)]"><Progress color={ProgressColor.GRAYSCALE_400} /><Text text={"\u0141adowanie koszyka"} color={TextColor.GRAYSCALE_400} fontSize={TextSize.S} /></div>;
    $[50] = loadedCart;
    $[51] = loadingCart;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== addressPagination || $[54] !== addresses || $[55] !== carriers || $[56] !== cart || $[57] !== checkoutState || $[58] !== handleBillingAddressPageChange || $[59] !== handleBillingAddressSearchChange || $[60] !== handleBillingAddressSelect || $[61] !== handleShippingAddressPageChange || $[62] !== handleShippingAddressSearchChange || $[63] !== handleShippingAddressSelect || $[64] !== handleVatInvoiceChange || $[65] !== hasAnyAddresses || $[66] !== hasVatAddresses || $[67] !== idCarrier || $[68] !== isAddressesLoading || $[69] !== isLoggedIn || $[70] !== isSignInFormOpen || $[71] !== isSignUpFormOpen || $[72] !== loadingCart || $[73] !== loadingCreateOrder || $[74] !== loadingGetPayments || $[75] !== loadingSetPickupPoint || $[76] !== payments || $[77] !== renderAddressSection || $[78] !== renderFilteredCarriersOptions || $[79] !== renderFilteredPaymentsOptions || $[80] !== router || $[81] !== signInForm || $[82] !== signUpForm) {
    t19 = cart && cart.productsCount > 0 && !loadingCart && <div className="relative w-full h-[calc(100vh-64px)]"><div className="hidden lg:block absolute top-0 h-full bg-gray-50" style={{
        left: "calc(50% + (min(100vw, 1536px) / 2) - 480px)",
        right: "0"
      }} /><div className="w-full max-w-screen-2xl mx-auto relative z-10"><div className="grid lg:grid-cols-[1fr_480px] grid-cols-1 lg:h-[calc(100vh-64px)]">{isLoggedIn && <div className="p-4 lg:p-8 flex flex-col lg:overflow-y-auto"><StepSection $isActive={checkoutState.isStepActive(CHECKOUT_STEPS.ADDRESS)} $isCompleted={checkoutState.isStepCompleted(CHECKOUT_STEPS.ADDRESS)}><StepHeader stepNumber={1} title="Wybierz adres" isActive={checkoutState.isStepActive(CHECKOUT_STEPS.ADDRESS)} isCompleted={checkoutState.isStepCompleted(CHECKOUT_STEPS.ADDRESS)} onEdit={() => checkoutState.resetStep(CHECKOUT_STEPS.ADDRESS)} />{isAddressesLoading ? <div className="flex flex-col gap-4 w-full h-[200px] justify-center items-center"><Progress color={ProgressColor.GRAYSCALE_400} /><Text text={"\u0141adowanie adres\xF3w"} fontSize={TextSize.S} color={TextColor.GRAYSCALE_400} /></div> : checkoutState.isStepActive(CHECKOUT_STEPS.ADDRESS) && !checkoutState.isStepCompleted(CHECKOUT_STEPS.ADDRESS) && <div className="flex flex-col gap-4">{renderAddressSection("shipping", addressPagination.filteredShippingAddresses, addressPagination.paginatedShippingAddresses, checkoutState.selectedShippingAddress, addressPagination.shippingAddressSearch, addressPagination.currentShippingAddressPage, addressPagination.totalShippingAddressPages, addressPagination.loadingShippingAddressList, handleShippingAddressSelect, handleShippingAddressSearchChange, handleShippingAddressPageChange, setOpenAddressModal)}{hasAnyAddresses && <Checkbox label="Faktura VAT" className="mb-4" id="vat-invoice" name="vat-invoice" checked={checkoutState.vatInvoice} onChange={handleVatInvoiceChange} justifyStart={true} />}{checkoutState.vatInvoice && hasVatAddresses && renderAddressSection("billing", addressPagination.filteredBillingAddresses, addressPagination.paginatedBillingAddresses, checkoutState.selectedBillingAddress, addressPagination.billingAddressSearch, addressPagination.currentBillingAddressPage, addressPagination.totalBillingAddressPages, addressPagination.loadingBillingAddressList, handleBillingAddressSelect, handleBillingAddressSearchChange, handleBillingAddressPageChange, setOpenAddressModal)}{checkoutState.vatInvoice && !hasVatAddresses && hasAnyAddresses && <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 mb-6"><div><Text text={"Brak adres\xF3w z danymi do faktury VAT"} fontSize={TextSize.S} weight={TextWeight.SEMIBOLD} color={TextColor.GRAYSCALE_700} className="mb-1" /><Text text={"Aby wystawi\u0107 faktur\u0119 VAT, musisz doda\u0107 adres z numerem NIP. Mo\u017Cesz to zrobi\u0107 po wybraniu adresu wysy\u0142ki."} fontSize={TextSize.XS} color={TextColor.GRAYSCALE_600} className="mb-4" /><Button text="Dodaj adres" variant={ButtonVariant.SECONDARY} onClick={() => setOpenAddressModal(true)} size={ButtonSize.XS} className="flex items-center gap-2 whitespace-nowrap" /></div></div>}</div>}{checkoutState.isStepCompleted(CHECKOUT_STEPS.ADDRESS) && <div className="flex flex-col gap-4">{checkoutState.vatInvoice && <div className="flex items-center gap-2 p-3 bg-blue-50"><Check className="text-blue-600 !w-4 !h-4" /><Text text="Faktura VAT zostanie wystawiona" fontSize={TextSize.S} color={TextColor.PRIMARY_500} /></div>}<div className={`grid grid-cols-1 ${checkoutState.vatInvoice ? "md:grid-cols-2" : "md:grid-cols-1"} gap-4`}><div><Text text={"Adres wysy\u0142ki"} variant={TextVariant.H6} weight={TextWeight.MEDIUM} className="mb-4" />{addresses.filter(address_3 => address_3.id === checkoutState.selectedShippingAddress).map(address_4 => <AddressCard key={`shipping-selected-${address_4.id}`} address={address_4} className="w-full cursor-pointer" showRadio={true} isSelected={checkoutState.selectedShippingAddress === address_4.id} onSelect={_temp6} radioName="shipping-address" />)}</div>{checkoutState.vatInvoice && checkoutState.selectedBillingAddress && <div><Text text="Adres faktury" variant={TextVariant.H6} weight={TextWeight.MEDIUM} className="mb-4" />{addresses.filter(address_5 => address_5.id === checkoutState.selectedBillingAddress).map(address_6 => <AddressCard key={`billing-selected-${address_6.id}`} address={address_6} className="w-full cursor-pointer" showRadio={true} isSelected={checkoutState.selectedBillingAddress === address_6.id} onSelect={_temp7} radioName="billing-address" />)}</div>}</div></div>}</StepSection><StepSection $isActive={checkoutState.isStepActive(CHECKOUT_STEPS.SHIPPING)} $isCompleted={checkoutState.isStepCompleted(CHECKOUT_STEPS.SHIPPING)}><StepHeader stepNumber={2} title={"Rodzaj przesy\u0142ki"} isActive={checkoutState.isStepActive(CHECKOUT_STEPS.SHIPPING)} isCompleted={checkoutState.isStepCompleted(CHECKOUT_STEPS.SHIPPING)} onEdit={() => checkoutState.resetStep(CHECKOUT_STEPS.SHIPPING)} />{checkoutState.isStepActive(CHECKOUT_STEPS.SHIPPING) && <div className="flex flex-col gap-4">{renderFilteredCarriersOptions(carriers || [], checkoutState.selectedShipping, checkoutState.isStepCompleted(CHECKOUT_STEPS.SHIPPING))}</div>}</StepSection><StepSection $isActive={checkoutState.isStepActive(CHECKOUT_STEPS.PAYMENT)} $isCompleted={checkoutState.isStepCompleted(CHECKOUT_STEPS.PAYMENT)}><StepHeader stepNumber={3} title={"Metoda p\u0142atno\u015Bci"} isActive={checkoutState.isStepActive(CHECKOUT_STEPS.PAYMENT)} isCompleted={checkoutState.isStepCompleted(CHECKOUT_STEPS.PAYMENT)} onEdit={() => checkoutState.resetStep(CHECKOUT_STEPS.PAYMENT)} />{checkoutState.isStepActive(CHECKOUT_STEPS.PAYMENT) && <div className="flex flex-col gap-4">{loadingGetPayments || loadingSetPickupPoint ? <div className="flex flex-col gap-4 w-full h-[200px] justify-center items-center"><Progress color={ProgressColor.GRAYSCALE_400} /><Text text={"\u0141adowanie metod p\u0142atno\u015Bci"} fontSize={TextSize.S} color={TextColor.GRAYSCALE_400} /></div> : renderFilteredPaymentsOptions(payments, checkoutState.selectedPayment, checkoutState.isStepCompleted(CHECKOUT_STEPS.PAYMENT), PaymentOption)}</div>}</StepSection><StepSection $isActive={checkoutState.isStepActive(CHECKOUT_STEPS.SUMMARY)} $isCompleted={checkoutState.isStepCompleted(CHECKOUT_STEPS.SUMMARY)}>{checkoutState.isStepActive(CHECKOUT_STEPS.SUMMARY) && <div className="flex flex-col gap-4 justify-between items-center mb-6"><Input label="Komentarz" placeholder={"Dodaj komentarz do zam\xF3wienia"} id="comment" name="comment" value={checkoutState.comment} icon={false} onChange={e_2 => checkoutState.setComment(e_2.target.value)} helperText={"Je\u017Celi chcesz doda\u0107 komentarz do swojego zam\xF3wienia, zapisz go poni\u017Cej."} className="h-24" /><div className="flex flex-col gap-1 items-center w-full"><Button text={"Zam\xF3wienie z obowi\u0105zkiem zap\u0142aty"} variant={ButtonVariant.PRIMARY} className="!px-12 !w-full !max-w-full" disabled={!checkoutState.isFormValid || loadingCreateOrder} loading={loadingCreateOrder} onClick={() => {
                    const selectedPayment = payments.find(payment => payment.id === checkoutState.selectedPayment);
                    if (selectedPayment) {
                      router.push(selectedPayment.action);
                    }
                  }} /><Text textHtml={"Klikaj\u0105c przycisk 'Zam\xF3wienie z obowi\u0105zkiem zap\u0142aty', akceptujesz nasz <a href='/regulamin' target='_blank'>regulaminem</a> oraz <a href='/polityka-prywatnosci' target='_blank'>polityk\u0105 prywatno\u015Bci</a>"} fontSize={TextSize.XS} color={TextColor.GRAYSCALE_500} className="text-center w-full mt-4 !max-w-[320px]" /></div></div>}</StepSection></div>}{!isLoggedIn && <div className="p-4 lg:p-8 flex flex-col lg:overflow-y-auto"><div className="flex flex-col justify-center items-center gap-4">{isSignInFormOpen && <div className="flex flex-col gap-8 items-center">{signInForm}<div className="flex flex-col items-center gap-4"><Text text="Nie masz jeszcze konta?" variant={TextVariant.H5} /><Button text={"Zarejestruj si\u0119"} variant={ButtonVariant.SECONDARY} onClick={() => {
                    setIsSignUpFormOpen(true);
                    setIsSignInFormOpen(false);
                  }} /></div></div>}{isSignUpFormOpen && <div className="flex flex-col gap-8 items-center">{signUpForm}<div className="flex flex-col items-center gap-4"><Text text={"Masz ju\u017C konto?"} variant={TextVariant.H5} /><Button text={"Zaloguj si\u0119"} variant={ButtonVariant.SECONDARY} onClick={() => {
                    setIsSignInFormOpen(true);
                    setIsSignUpFormOpen(false);
                  }} /></div></div>}</div></div>}<div className="p-4 lg:p-8 lg:p-12 bg-gray-50 lg:bg-transparent lg:overflow-y-auto"><OrderSummary cart={cart} loadingCart={loadingCart} isFormValid={!!checkoutState.isFormValid && isLoggedIn} loadingCreateOrder={loadingCreateOrder} idCarrier={idCarrier} orderAction={() => {
              if (!!checkoutState.isFormValid && isLoggedIn) {
                const selectedPayment_0 = payments.find(payment_0 => payment_0.id === checkoutState.selectedPayment);
                if (selectedPayment_0) {
                  router.push(selectedPayment_0.action);
                }
              } else {
                console.log("[Checkout] Formularz wymaga uzupe\u0142nienia");
              }
            }} /><OrderSection cartQuantity={cart.productsCount} products={cart?.products || []} /></div></div></div></div>;
    $[53] = addressPagination;
    $[54] = addresses;
    $[55] = carriers;
    $[56] = cart;
    $[57] = checkoutState;
    $[58] = handleBillingAddressPageChange;
    $[59] = handleBillingAddressSearchChange;
    $[60] = handleBillingAddressSelect;
    $[61] = handleShippingAddressPageChange;
    $[62] = handleShippingAddressSearchChange;
    $[63] = handleShippingAddressSelect;
    $[64] = handleVatInvoiceChange;
    $[65] = hasAnyAddresses;
    $[66] = hasVatAddresses;
    $[67] = idCarrier;
    $[68] = isAddressesLoading;
    $[69] = isLoggedIn;
    $[70] = isSignInFormOpen;
    $[71] = isSignUpFormOpen;
    $[72] = loadingCart;
    $[73] = loadingCreateOrder;
    $[74] = loadingGetPayments;
    $[75] = loadingSetPickupPoint;
    $[76] = payments;
    $[77] = renderAddressSection;
    $[78] = renderFilteredCarriersOptions;
    $[79] = renderFilteredPaymentsOptions;
    $[80] = router;
    $[81] = signInForm;
    $[82] = signUpForm;
    $[83] = t19;
  } else {
    t19 = $[83];
  }
  let t20;
  if ($[84] !== cart || $[85] !== loadedCart || $[86] !== loadingCart) {
    t20 = !cart && !loadingCart && loadedCart && <div className="flex justify-center items-center h-full"><Text text={"Nie uda\u0142o si\u0119 za\u0142adowa\u0107 koszyka"} /></div>;
    $[84] = cart;
    $[85] = loadedCart;
    $[86] = loadingCart;
    $[87] = t20;
  } else {
    t20 = $[87];
  }
  let t21;
  if ($[88] !== cart || $[89] !== loadingCart || $[90] !== router) {
    t21 = cart && cart.productsCount === 0 && !loadingCart && <div className="flex flex-col gap-4 justify-center items-center h-full min-h-[calc(50vh-64px)]"><Button text={"Przejd\u017A do sklepu"} variant={ButtonVariant.SECONDARY} size={ButtonSize.XS} onClick={() => router.push("/")} className="!px-12 w-full" /><Text text="Koszyk jest pusty" color={TextColor.GRAYSCALE_400} fontSize={TextSize.S} /></div>;
    $[88] = cart;
    $[89] = loadingCart;
    $[90] = router;
    $[91] = t21;
  } else {
    t21 = $[91];
  }
  let t22;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => setOpenAddressModal(false);
    $[92] = t22;
  } else {
    t22 = $[92];
  }
  const t23 = !isMobile;
  let t24;
  if ($[93] !== isMobile) {
    t24 = !isMobile ? {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "100%",
      maxWidth: "800px"
    } : {};
    $[93] = isMobile;
    $[94] = t24;
  } else {
    t24 = $[94];
  }
  const t25 = addressDetails ? "Edytuj adres" : "Dodaj adres";
  let t26;
  if ($[95] !== t25) {
    t26 = <Text text={t25} variant={TextVariant.H6} weight={TextWeight.MEDIUM} className="mb-4" />;
    $[95] = t25;
    $[96] = t26;
  } else {
    t26 = $[96];
  }
  let t27;
  if ($[97] !== addressDetails || $[98] !== addressForm) {
    t27 = addressForm && addressForm({
      closeModal: () => {
        setOpenAddressModal(false);
        setAddressDetails(undefined);
      },
      addressDetails
    });
    $[97] = addressDetails;
    $[98] = addressForm;
    $[99] = t27;
  } else {
    t27 = $[99];
  }
  let t28;
  if ($[100] !== t26 || $[101] !== t27) {
    t28 = <>{t26}{t27}</>;
    $[100] = t26;
    $[101] = t27;
    $[102] = t28;
  } else {
    t28 = $[102];
  }
  let t29;
  if ($[103] !== openAddressModal || $[104] !== t23 || $[105] !== t24 || $[106] !== t28) {
    t29 = <Modal open={openAddressModal} onClose={t22} fullPageMobile={t23} modalBoxStyles={t24}>{t28}</Modal>;
    $[103] = openAddressModal;
    $[104] = t23;
    $[105] = t24;
    $[106] = t28;
    $[107] = t29;
  } else {
    t29 = $[107];
  }
  let t30;
  if ($[108] !== t17 || $[109] !== t18 || $[110] !== t19 || $[111] !== t20 || $[112] !== t21 || $[113] !== t29) {
    t30 = <div className="relative w-full min-h-[100vh]">{t17}{t18}{t19}{t20}{t21}{t29}</div>;
    $[108] = t17;
    $[109] = t18;
    $[110] = t19;
    $[111] = t20;
    $[112] = t21;
    $[113] = t29;
    $[114] = t30;
  } else {
    t30 = $[114];
  }
  return t30;
};

// Styled Components
const StepSection = styled.div`
  margin-bottom: 2rem;
  opacity: ${({
  $isActive
}) => $isActive ? 1 : 0.5};
  transition: opacity 0.3s ease;
`;
export default Checkout;
function _temp(address) {
  return address.vatNumber !== "";
}
function _temp2(address2) {
  return address2 && address2.trim() !== "";
}
function _temp3(currentPage, totalPages) {
  const pages = [];
  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    if (currentPage <= 3) {
      for (let i_0 = 1; i_0 <= 4; i_0++) {
        pages.push(i_0);
      }
      pages.push("...");
      pages.push(totalPages);
    } else {
      if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i_1 = totalPages - 3; i_1 <= totalPages; i_1++) {
          pages.push(i_1);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i_2 = currentPage - 1; i_2 <= currentPage + 1; i_2++) {
          pages.push(i_2);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }
  }
  return pages;
}
function _temp4(option_0) {
  return option_0.callToActionText === "Zap\u0142a\u0107 got\xF3wk\u0105 przy odbiorze";
}
function _temp5(option_1) {
  return option_1.callToActionText !== "Zap\u0142a\u0107 got\xF3wk\u0105 przy odbiorze";
}
function _temp6() {}
function _temp7() {}